import React, { useState } from 'react';

import styles from './Login.module.css';

import FormLayout from './FormLayout';
import {
  containsLowercaseLetter,
  containsUppercaseLetter,
  containsNumber,
  containsSpecialCharacter,
  isMinLength,
  isPasswordFormatValid
} from './authHelpers';
import PasswordField from './PasswordField';
import PasswordHelper from './PasswordHelper';

interface NewPasswordFormProps {
  onSubmit: (newPassword: string) => void;
  loading: boolean;
  errorMessage: string;
}

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  onSubmit,
  loading,
  errorMessage
}) => {
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [meetsMinLength, setMeetsMinLength] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);

  React.useEffect(() => {
    const match = newPassword === newPasswordConfirm;
    if (newPasswordConfirm != '' && !match) {
      setNewPasswordError('Passwords do not match.');
    } else {
      setNewPasswordError('');
    }
    setIsValid(match && isPasswordFormatValid(newPassword));
  }, [newPassword, newPasswordConfirm]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onSubmit(newPassword);
  };

  const handlePasswordChange = (password: string): void => {
    setNewPassword(password);
    setMeetsMinLength(isMinLength(password));
    setHasUppercase(containsUppercaseLetter(password));
    setHasLowercase(containsLowercaseLetter(password));
    setHasNumber(containsNumber(password));
    setHasSpecialChar(containsSpecialCharacter(password));
  };

  return (
    <FormLayout
      title="Create a new password"
      errorMessage={errorMessage}
      message=""
    >
      <div>
        <form onSubmit={handleSubmit}>
          <div data-testid="account-disclosures" className={styles['registration-disclosure']}>
            Proprio, Inc. may send you email and SMS communications 
            for case notifications and security purposes.
            <br/><br/>
            Message and data rates may apply. Expect one message per sign-in.
            <br/><br/>
            By completing your registration, you agree to our{' '}
            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className={styles['disclosure-link']}>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles['disclosure-link']}>
              Privacy Policy
            </a>. You may opt out at any time.
            
          </div>

          <PasswordField
            label="New Password:"
            onPasswordChange={(pwd: string) => handlePasswordChange(pwd)}
          />

          <PasswordHelper
            meetsMinLength={meetsMinLength}
            hasUppercase={hasUppercase}
            hasLowercase={hasLowercase}
            hasNumber={hasNumber}
            hasSpecialChar={hasSpecialChar}
          />

          <PasswordField
            label="Confirm New Password:"
            onPasswordChange={(pwd: string) => setNewPasswordConfirm(pwd)}
            showError={false}
          />
          {newPasswordError && <div className={styles['login-error-message']}>{newPasswordError}</div>}

        </form>
      </div>

      <div className={styles['login-action-container']}>
        <button
          type="submit"
          className={styles['login-submit-btn']}
          data-testid="submit-btn"
          disabled={(loading || !isValid)}
          onClick={handleSubmit}
        >
          Set New Password
        </button>
      </div>
    </FormLayout>
  );
};

export default NewPasswordForm;
